import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold tracking-tight">SAANJH</h1>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-4">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing and using SAANJH ("Service"), you accept and agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Service Description</h2>
            <p className="text-muted-foreground leading-relaxed">
              SAANJH provides a QR-powered e-commerce platform that enables vendors to create online stores, 
              manage products, and accept orders. We facilitate connections between vendors and customers but 
              are not party to the actual transactions between them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
            <h3 className="text-xl font-medium mb-3">3.1 Vendor Accounts</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
              <li>You must provide accurate and complete information during registration</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must not use another user's account without permission</li>
              <li>You must notify us immediately of any unauthorized use</li>
            </ul>

            <h3 className="text-xl font-medium mb-3">3.2 Customer Accounts</h3>
            <p className="text-muted-foreground leading-relaxed">
              Customer accounts are created automatically upon first purchase. By making a purchase, customers 
              agree to these terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Subscription and Payments</h2>
            <h3 className="text-xl font-medium mb-3">4.1 Subscription Plans</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
              <li>Monthly Plan: ₹123 per month</li>
              <li>Annual Plan: ₹1,113 per year</li>
              <li>30-day free trial available for new vendors</li>
              <li>All prices are in Indian Rupees (INR) and inclusive of applicable taxes</li>
            </ul>

            <h3 className="text-xl font-medium mb-3">4.2 Payment Processing</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              All payments are processed through Razorpay Software Private Limited, a RBI-approved payment 
              gateway. SAANJH does not store your payment information. By making a payment, you agree to 
              Razorpay's terms and conditions.
            </p>

            <h3 className="text-xl font-medium mb-3">4.3 Refund Policy</h3>
            <p className="text-muted-foreground leading-relaxed">
              Subscription fees are non-refundable except as required by law. If you cancel your subscription, 
              you will retain access until the end of your billing period.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Vendor Responsibilities</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide accurate product information and pricing</li>
              <li>Fulfill orders in a timely manner</li>
              <li>Respond to customer inquiries</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not sell prohibited, illegal, or counterfeit items</li>
              <li>Maintain appropriate licenses for your business</li>
              <li>Handle customer data responsibly</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Prohibited Uses</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You may not use SAANJH to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Transmit malware or malicious code</li>
              <li>Engage in fraudulent activities</li>
              <li>Harass, abuse, or harm others</li>
              <li>Sell prohibited items (weapons, drugs, etc.)</li>
              <li>Interfere with the Service's operation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Content Ownership</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You retain ownership of all content you upload (product images, descriptions, custom pages). 
              By uploading content, you grant SAANJH a license to display this content on the platform and 
              in promotional materials.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Service Modifications</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify, suspend, or discontinue any part of the Service with or without 
              notice. We are not liable for any modification, suspension, or discontinuation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Account Termination</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We may terminate or suspend your account immediately if:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>You violate these Terms of Service</li>
              <li>Your account is used for fraudulent activities</li>
              <li>You fail to pay subscription fees</li>
              <li>Required by law or regulatory authorities</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              SAANJH is provided "as is" without warranties of any kind. We are not liable for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
              <li>Disputes between vendors and customers</li>
              <li>Product quality or delivery issues</li>
              <li>Loss of revenue or profits</li>
              <li>Service interruptions or data loss</li>
              <li>Third-party actions (including Razorpay)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              Our total liability shall not exceed the amount paid by you in the past 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify and hold SAANJH harmless from any claims, damages, or expenses arising 
              from your use of the Service, violation of these terms, or infringement of third-party rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Dispute Resolution</h2>
            <p className="text-muted-foreground leading-relaxed">
              Any disputes arising from these terms shall be governed by the laws of India. Disputes shall 
              be resolved through arbitration in Ludhiana, Punjab, in accordance with the Arbitration and 
              Conciliation Act, 1996.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">13. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update these Terms of Service at any time. Continued use of the Service after changes 
              constitutes acceptance of the new terms. We will notify you of significant changes via email 
              or platform notification.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">14. Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              For questions about these Terms of Service, contact us:
            </p>
            <div className="bg-secondary p-6 rounded-lg">
              <p className="font-medium mb-2">SAANJH</p>
              <p className="text-muted-foreground">Head Office, Sant Nagar, Civil Lines</p>
              <p className="text-muted-foreground">Ludhiana - 141001, Punjab, India</p>
              <p className="text-muted-foreground mt-3">Email: admin@saanjh.cc</p>
              <p className="text-muted-foreground">Phone: +91 78376 40500</p>
            </div>
          </section>

          <section className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 text-amber-900">Important Payment Notice</h3>
            <p className="text-sm text-amber-800 leading-relaxed">
              All transactions on SAANJH are subject to Razorpay's terms and conditions. Razorpay is an 
              independent payment service provider approved by the Reserve Bank of India. For payment-related 
              disputes, please refer to Razorpay's dispute resolution process at 
              <a href="https://razorpay.com/support/" target="_blank" rel="noopener noreferrer" 
                 className="text-amber-900 font-medium hover:underline ml-1">
                razorpay.com/support
              </a>
            </p>
          </section>
        </div>
      </div>

      <footer className="border-t py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} SAANJH. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;